import { useLazyQuery } from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Badge, Col, Input, Label, Row } from 'reactstrap';
import InputField from '../../../components/layout/InputField';
import Loader from '../../../components/layout/Loader';
import NoDataFound from '../../../components/layout/NoDataFound';
import Pagination from '../../../components/layout/Pagination';
import TableComponent from '../../../components/layout/TableComponent';
import { MY_EARNING_HEADER, PAGE_LIMIT } from '../../../utils/constant';
import { moneyFormatter } from '../../../utils/helper';
import {
  MY_EARNING_COMMISSIONS,
  MyEarningFilter,
  MyEarningQueryData,
  MyEarningRow,
} from './gql';
import { formatLedgerDate, formatPayoutMonth, formatRate, formatSide } from './utils';
import './myEarning.scss';

let time = 0;

// Default range = the current calendar year (e.g. 1/1/2026 – 12/31/2026).
const CURRENT_YEAR = new Date().getFullYear();
const DEFAULT_FROM_DATE = `${CURRENT_YEAR}-01-01`;
const DEFAULT_TO_DATE = `${CURRENT_YEAR}-12-31`;

// TableComponent emits the header LABEL + 'Sort' (e.g. "Start DateSort"),
// so map the labels back onto the column names the API sorts on.
const SORT_COLUMN_BY_LABEL: { [key: string]: string } = {
  ID: 'placementId',
  Company: 'companyName',
  Candidate: 'candidateName',
  'Job Title': 'jobTitle',
  'Start Date': 'startDate',
  Status: 'status',
  Commission: 'commissionAmount',
  'Invoice #': 'invoiceNumber',
  Payout: 'payout',
};

interface MyEarningColumnFilters {
  idSearch: number | null;
  companySearch: string;
  candidateSearch: string;
  jobTitleSearch: string;
  invoiceSearch: string;
  statusSearch: string;
  startDateSearch: string;
  sorting: {
    columnName: string;
    columnValue: string;
  };
}

const fieldNames = [
  'idSearch',
  'companySearch',
  'candidateSearch',
  'jobTitleSearch',
  'invoiceSearch',
  'statusSearch',
  'startDateSearch',
];

const defaultColumnFilters: MyEarningColumnFilters = {
  idSearch: null,
  companySearch: '',
  candidateSearch: '',
  jobTitleSearch: '',
  invoiceSearch: '',
  statusSearch: '',
  startDateSearch: '',
  sorting: {
    columnName: '',
    columnValue: '',
  },
};

const MyEarning = () => {
  const { register, setValue, control } = useForm();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [columnFilters, setColumnFilters] =
    useState<MyEarningColumnFilters>(defaultColumnFilters);
  const [isSetTimeOut, setIsSetTime] = useState<boolean>(true);
  const [errorMsg, setErrorMsg] = useState({});

  const [fromDate, setFromDate] = useState<string>(DEFAULT_FROM_DATE);
  const [toDate, setToDate] = useState<string>(DEFAULT_TO_DATE);

  const [getMyEarnings, { data, loading }] = useLazyQuery<MyEarningQueryData>(
    MY_EARNING_COMMISSIONS,
    { fetchPolicy: 'network-only' }
  );

  const changeFiled = (fieldName: string, value: any) => {
    if (fieldName?.endsWith('Sort')) {
      const label = fieldName.replace('Sort', '');
      const columnName = SORT_COLUMN_BY_LABEL[label];
      if (!columnName) {
        return;
      }
      setIsSetTime(true);
      setColumnFilters((prevFilters) => ({
        ...prevFilters,
        sorting: {
          columnName,
          columnValue: value ? 'ASC' : 'DESC',
        },
      }));
    } else {
      setCurrentPage(1);
      setIsSetTime(false);
      setColumnFilters((prevFilters) => ({
        ...prevFilters,
        [fieldName]: value || (fieldName === 'idSearch' ? null : ''),
      }));
    }
  };

  const getMyEarningList = useCallback(async () => {
    const filters: MyEarningFilter = {};

    // The header input allows '.' and '-', but idSearch is a GraphQL Int:
    // a decimal would fail validation and silently blank the table, so only
    // send a non-negative whole number and otherwise leave the filter off.
    if (columnFilters?.idSearch) {
      const id = Number(columnFilters.idSearch);
      if (Number.isInteger(id) && id >= 0) {
        filters.idSearch = id;
      }
    }
    if (columnFilters?.companySearch?.trim()) {
      filters.companySearch = columnFilters.companySearch.trim();
    }
    if (columnFilters?.candidateSearch?.trim()) {
      filters.candidateSearch = columnFilters.candidateSearch.trim();
    }
    if (columnFilters?.jobTitleSearch?.trim()) {
      filters.jobTitleSearch = columnFilters.jobTitleSearch.trim();
    }
    if (columnFilters?.invoiceSearch?.trim()) {
      filters.invoiceSearch = columnFilters.invoiceSearch.trim();
    }
    if (columnFilters?.statusSearch) {
      filters.statusSearch = columnFilters.statusSearch;
    }
    if (columnFilters?.startDateSearch) {
      filters.startDateSearch = columnFilters.startDateSearch;
    }
    if (fromDate) {
      filters.fromDate = fromDate;
    }
    if (toDate) {
      filters.toDate = toDate;
    }
    // The date range always applies to the start date.
    if (fromDate || toDate) {
      filters.dateType = 'startDate';
    }
    // Server defaults to Start Date ASC, so nothing is sent until a column is clicked.
    if (columnFilters?.sorting?.columnName) {
      filters.sorting = { ...columnFilters.sorting };
    }

    const variables = {
      payload: { limit, page: currentPage },
      filters,
    };

    if (isSetTimeOut) {
      getMyEarnings({ variables });
    } else {
      window.clearTimeout(time);
      time = window.setTimeout(async () => {
        getMyEarnings({ variables });
      }, 800);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, limit, columnFilters, fromDate, toDate]);

  useEffect(() => {
    getMyEarningList();
    // eslint-disable-next-line
  }, [getMyEarningList]);

  // The tab unmounts when the user switches away — drop any pending debounce.
  useEffect(() => {
    return () => {
      window.clearTimeout(time);
    };
  }, []);

  const clearFilters = () => {
    if (columnFilters?.sorting?.columnName) {
      setValue(columnFilters?.sorting?.columnName, false);
    }
    setIsSetTime(true);
    setErrorMsg({});
    setFromDate(DEFAULT_FROM_DATE);
    setToDate(DEFAULT_TO_DATE);
    setCurrentPage(1);
    setColumnFilters(defaultColumnFilters);
    fieldNames.forEach((fieldName) => {
      setValue(fieldName, defaultColumnFilters[fieldName]);
    });
  };

  const result = data?.myEarningCommissions;
  const rows: MyEarningRow[] = result?.rows ?? [];

  return (
    <div className="admin-tabs team-tabs my-earning-wrap">
      <div className="search-filter my-earning-filters">
        <Row className="w-100">
          <Col xl="3" lg="4" md="6" xs="12">
            <div className="form-group">
              <Label for="myEarningFromDate">Start Date From</Label>
              <Input
                id="myEarningFromDate"
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setIsSetTime(true);
                  setCurrentPage(1);
                  setFromDate(e.target.value);
                }}
              />
            </div>
          </Col>

          <Col xl="3" lg="4" md="6" xs="12">
            <div className="form-group">
              <Label for="myEarningToDate">Start Date To</Label>
              <Input
                id="myEarningToDate"
                type="date"
                value={toDate}
                onChange={(e) => {
                  setIsSetTime(true);
                  setCurrentPage(1);
                  setToDate(e.target.value);
                }}
              />
            </div>
          </Col>

          <Col xl="3" lg="4" md="6" xs="12">
            <div className="form-group my-earning-clear">
              <button
                type="button"
                className="buttonGenericStyle filledButton"
                onClick={clearFilters}
              >
                Clear
              </button>
            </div>
          </Col>
        </Row>
      </div>

      <div className="my-earning-totals">
        <div className="my-earning-total-box">
          <span className="my-earning-total-label">Total Projected</span>
          <span className="my-earning-total-value">
            {moneyFormatter(result?.totalProjected ?? 0)}
          </span>
        </div>
        <div className="my-earning-total-box">
          <span className="my-earning-total-label">Total Received</span>
          <span className="my-earning-total-value">
            {moneyFormatter(result?.totalEarned ?? 0)}
          </span>
        </div>
      </div>

      <p className="my-earning-disclaimer" role="note">
        This is an estimate of your earnings. For an accurate report, please refer
        to your activity reports.
      </p>

      <div className="candidatePipelineTable minimize-pading-from-table">
        <TableComponent
          advanceSearching={true}
          tableHeaderAdvSeacrh={MY_EARNING_HEADER}
          handleChange={(filedName: string, value: any) =>
            changeFiled(filedName, value)
          }
          columnFilters={columnFilters}
          register={register}
          setValue={setValue}
          control={control}
          tableName="MyEarning"
          setErrorMsg={setErrorMsg}
          errorMsg={errorMsg}
        >
          {loading && (
            <tbody>
              <tr>
                <td colSpan={12}>
                  <Loader loaderClass="sm-height " />
                </td>
              </tr>
            </tbody>
          )}

          {!loading && !rows?.length && (
            <tbody>
              <tr>
                <td colSpan={12} className="empty-table-td">
                  <NoDataFound text="No Commission Records Found!" />
                </td>
              </tr>
            </tbody>
          )}

          <tbody>
            {!loading &&
              rows?.map((item: MyEarningRow) => {
                const isClawback = item?.entryType === 'CLAWBACK';

                return (
                  <tr key={item?.id}>
                    <td>{item?.placementId ?? '--'}</td>
                    <td className="text-truncate max-200">
                      {item?.companyName || '--'}
                    </td>
                    <td className="text-truncate max-200">
                      {item?.candidateName || '--'}
                    </td>
                    <td className="text-truncate max-200">
                      {item?.jobTitle || '--'}
                    </td>
                    <td>{formatLedgerDate(item?.startDate) || '--'}</td>
                    <td>{item?.invoiceNumber ?? '--'}</td>
                    <td>{formatLedgerDate(item?.dueDate)}</td>
                    <td>{formatRate(item)}</td>
                    <td className="badge-column">
                      {isClawback ? (
                        <Badge color="danger" className="text-capitalize">
                          Clawback
                        </Badge>
                      ) : (
                        <Badge
                          color={item?.status === 'EARNED' ? 'success' : 'warning'}
                          className="text-capitalize"
                        >
                          {item?.status === 'EARNED' ? 'Received' : 'Projected'}
                        </Badge>
                      )}
                    </td>
                    <td>{formatSide(item)}</td>
                    <td className={isClawback ? 'text-danger' : ''}>
                      {moneyFormatter(item?.commissionAmount ?? 0)}
                    </td>
                    <td>{formatPayoutMonth(item?.payout)}</td>
                  </tr>
                );
              })}
          </tbody>
        </TableComponent>
      </div>

      {rows?.length ? (
        <div className="users-pagination">
          <Pagination
            onPageChange={(pageClicked: number) => {
              setIsSetTime(true);
              setCurrentPage(pageClicked);
            }}
            pageCount={Math.ceil((result?.count ?? 0) / limit)}
            currentPage={currentPage}
          />

          <div className="d-flex align-items-center">
            <div className="pagination-display paginationItemsSelect">
              <p className="m-0">
                {(currentPage - 1) * limit + 1} to{' '}
                {Math.min(currentPage * limit, result?.count ?? 0)} of{' '}
                {result?.count ?? 0}
              </p>

              <div className="divider"></div>

              <label>Display</label>

              <InputField
                label=""
                inputtype="select"
                selectItems={PAGE_LIMIT.filter((item) => item.value !== limit)}
                inputid="limit"
                placeholder={limit.toString()}
                inputMethod={(value: string) => {
                  setIsSetTime(true);
                  setCurrentPage(1);
                  setLimit(parseInt(value));
                }}
              />
            </div>
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default MyEarning;
